"use client";

import { useRef } from "react";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import CallExpandBtn from "./CallExpandBtn";
import CustomButton from "./CustomButton";
import YoutubeExpandBtn from "./YoutubeExpandBtnc";
import { ThemeName } from "./theme";
import { reactNodeToText } from "@/lib/utils";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import { ROUTES } from "@/lib/routes";
import { trackBookAppointmentClicked } from "@/lib/analytics";

// Service types for deep linking to booking flow
type BookingServiceType = "counselling" | "life-coach" | "occupational-therapy" | "psychology";

export interface HeroCard {
  src: string;
  label: string;
  href: string;
}

// lg (1024px–1439px): compact size; 3xl (1440px+): full size
const CARD_SIZES = {
  lg: { width: 200, height: 175, gap: 10 },
  "3xl": { width: 260, height: 228, gap: 14 },
};

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [query]);

  return matches;
};

const ServiceCards = ({
  cards,
  title,
}: {
  cards: HeroCard[];
  title?: React.ReactNode;
}) => {
  const [hovered, setHovered] = useState(false);
  const isXl = useMediaQuery("(min-width: 1440px)");
  const { width: CARD_W, height: CARD_H, gap: GAP } = CARD_SIZES.lg;
  const totalCards = cards.length;

  if (totalCards === 0) return null;

  // Dynamically compute transforms based on actual number of cards
  const transforms = hovered
    ? cards.map((_, i) => ({ x: i * (CARD_W + GAP), rotate: 0, zIndex: 3 }))
    : cards.map((_, i) => ({
        x: 0,
        rotate: (totalCards - 1 - i) * 7,
        zIndex: i + 1,
      }));

  return (
    <div className="absolute bottom-0 left-[15%] z-10 hidden flex-col gap-4 pb-[50px] pt-[30px] md:left-0 md:flex md:px-[31px]">
      {title && (
        <span className="mb-5 font-onest text-2xl font-bold uppercase tracking-[0.2em] text-white">
          {title}
        </span>
      )}
      <div
        className="relative"
        style={{
          width: hovered
            ? totalCards * CARD_W + (totalCards - 1) * GAP
            : CARD_W + 48,
          height: CARD_H,
          transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {cards.map((card, i) => (
          <Link
            key={i}
            href={card.href}
            className="group absolute overflow-hidden rounded-[10px] border border-white/30 bg-white/20 p-1.5 backdrop-blur-sm"
            style={{
              width: CARD_W,
              height: CARD_H,
              top: 0,
              left: 0,
              zIndex: transforms[i]?.zIndex ?? 3,
              transformOrigin: "50% 100%",
              transform: `translateX(${transforms[i]?.x ?? 0}px) rotate(${transforms[i]?.rotate ?? 0}deg)`,
              transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            <div className="relative h-full w-full overflow-hidden rounded-[7px]">
              <Image
                src={card.src}
                alt={card.label}
                fill
                className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-110"
                sizes={`${CARD_W}px`}
              />
              {/* Blue gradient overlay: dark blue at bottom → transparent at 50% */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#003480e6] from-[0%] to-transparent to-[50%]" />
              {/* Label + arrow */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-[14px] py-[14px]">
                <span className="font-onest text-sm font-medium text-white">
                  {card.label}
                </span>
                <ArrowRight size={14} className="shrink-0 text-white" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

interface HeroSectionProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  enableButtons?: boolean;
  enableCards?: boolean;
  cardsList?: HeroCard[];
  bgImage?: string;
  theme?: Exclude<ThemeName, "white">;
  content?: React.ReactNode;
  cardsTitle?: React.ReactNode;
  youtubeLink?: string;
  circularImage?: string;
  /** Optional service type to pre-select in booking flow */
  bookingService?: BookingServiceType;
}

const HeroSection = ({
  title,
  theme,
  subtitle,
  enableButtons = true,
  enableCards = true,
  cardsList = [],
  bgImage = "/images/v3/home/home-hero.png",
  content,
  cardsTitle,
  youtubeLink,
  circularImage,
  bookingService,
}: HeroSectionProps) => {
  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;
  const pathname = usePathname();
  const isCourseDiscovery = pathname === "/24seven-self-discovery";
  const contentRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const els = Array.from(
        contentRef.current?.children ?? [],
      ) as HTMLElement[];
      if (!els.length) return;
      gsap.set(els, { y: 36, opacity: 0 });
      gsap.to(els, {
        y: 0,
        opacity: 1,
        duration: 0.75,
        ease: "power2.out",
        stagger: 0.15,
        delay: 0.2,
      });
    },
    { scope: contentRef },
  );

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: reactNodeToText(title),
    ...(subtitle ? { description: reactNodeToText(subtitle) } : {}),
    url: new URL(pathname, SITE_URL).href,
    isPartOf: {
      "@type": "WebSite",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
  };

  return (
    <section className="relative h-[650px] w-full overflow-hidden bg-black md:h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pageSchema).replace(/</g, "\\u003c"),
        }}
      />
      {/* Background */}
      <Image
        src={bgImage}
        alt="Hero background"
        fill
        priority
        quality={100}
        className="object-cover object-center"
      />
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_280%_100%_at_50%_0%,transparent_68%,black_100%)]" />

      {/* Main content — above decorative layers; only the inner box captures pointer events */}
      <div className="pointer-events-none relative z-10 mt-6 flex h-full flex-col items-center justify-center md:pt-[87px] lg:mt-0">
        <div
          ref={contentRef}
          className="pointer-events-auto flex w-full max-w-[1125px] flex-col items-center gap-[27px] px-6"
        >
          {circularImage && (
            <div
              className="h-[200px] w-[200px] shrink-0 md:h-[240px] md:w-[240px]"
              style={{
                borderRadius: "309px",
                border: "4px solid #FFF",
                backgroundImage: `url(${circularImage})`,
                backgroundPosition: "50% 50%",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundColor: "lightgray",
                boxShadow: "0 0 40px 0 #FFF inset",
              }}
            />
          )}
          <h1
            className="text-center font-crimsonText font-bold leading-[1] text-[#F2F7FF]"
            style={{
              fontSize: "clamp(36px, 4vw, 80px)",
              textShadow: "0 0 40px rgba(0,0,0,0.4)",
            }}
          >
            {title}
          </h1>

          {subtitle && (
            <p
              className="max-w-[891px] text-center font-onest font-light leading-[1.75] text-white"
              style={{ fontSize: "clamp(16px, 1.5vw, 24px)" }}
            >
              {subtitle}
            </p>
          )}

          {enableButtons && (
            <div className="flex flex-wrap items-center justify-center gap-5">
              {youtubeLink ? (
                <YoutubeExpandBtn variant="light" />
              ) : (
                (!isCourseDiscovery && <CallExpandBtn variant="light" />)
              )}
              {isCourseDiscovery ? (
                <CustomButton
                  label="Buy Now"
                  href="https://courses.alexrodriguez.com.au/enroll/2977322?price_id=3831485"
                  variant="primary"
                  theme="blue"
                  withIcon
                />
              ) : (
                <CustomButton
                  href={bookingUrl}
                  theme={theme}
                  variant="primary"
                  label="Book an Appointment"
                  size="xl"
                  onClick={() =>
                    trackBookAppointmentClicked({
                      location: "hero_section",
                      service: bookingService,
                    })
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
      <Image
        src="/images/v3/rope-line.svg"
        alt=""
        width={1920}
        height={627}
        className="pointer-events-none absolute bottom-0 z-[1] w-full"
        aria-hidden
      />

      {enableCards && cardsList.length > 0 && (
        <ServiceCards cards={cardsList} title={cardsTitle} />
      )}
      {content && content}
    </section>
  );
};

export default HeroSection;
